/**
 * AZURE AD AUTHENTICATION CONFIGURATION
 *
 * Microsoft Authentication Library (MSAL) configuration for the
 * Home Language Survey application. This file defines:
 *
 * - Azure AD tenant and application registration details
 * - Authentication scopes and permissions
 * - Token caching and storage preferences
 * - Microsoft Graph API endpoint configuration
 *
 * Security Features:
 * - Single sign-on (SSO) with Vermont state Azure AD
 * - Secure token storage using localStorage
 * - Automatic token refresh and validation
 * - Integration with Microsoft Graph for user profiles
 *
 * This configuration enables authenticated access to administrative
 * features while maintaining FERPA compliance for student data protection.
 */

/**
 * MSAL CONFIGURATION OBJECT
 *
 * Core configuration for Azure AD authentication including:
 * - Client ID: Registered application identifier in Azure AD
 * - Authority: Vermont state tenant-specific authentication endpoint
 * - Redirect URI: Dynamic redirect based on current application URL
 * - Cache settings: Local storage for token persistence
 */
export const msalConfig = {
  auth: {
    // Application (client) ID registered in Azure AD
    clientId: '8a97f476-d0b7-4754-bf13-88a08e03ff54',
    // Vermont state Azure AD tenant authority URL
    authority:
      'https://login.microsoftonline.com/20b4933b-baad-433c-9c02-70edcc7559c6',
    // Dynamic redirect URI for flexible deployment environments
    redirectUri: window.location.toString(),
  },
  cache: {
    // Store authentication tokens in browser localStorage
    cacheLocation: 'localStorage',
    // Disable cookie storage for auth state (not needed for modern browsers)
    storeAuthStateInCookie: false,
    // Use secure cookies in production (set to true for HTTPS environments)
    secureCookies: false,
  },
}

/**
 * LOGIN REQUEST SCOPES
 *
 * Defines the Microsoft Graph API permissions requested during authentication.
 * These scopes determine what user data the application can access:
 *
 * - User.Read: Basic user profile information (name, email, etc.)
 * - openid: OpenID Connect authentication standard compliance
 *
 * Additional scopes can be added here if the application needs access
 * to other Microsoft services (Calendar, Mail, etc.)
 */
export const loginRequest = {
  scopes: ['User.Read', 'openid'],
}

/**
 * MICROSOFT GRAPH API CONFIGURATION
 *
 * Endpoint configuration for Microsoft Graph API services.
 * Currently configured for basic user profile access.
 *
 * Microsoft Graph provides access to:
 * - User profiles and organizational data
 * - Office 365 services integration
 * - Azure AD directory information
 *
 * The base endpoint can be extended with specific API paths as needed.
 */
export const graphConfig = {
  // Base URL for Microsoft Graph API calls
  graphMeEndpoint: 'https://graph.microsoft.com',
}
